/**
 * Assessment History List
 * First screen for each evaluation: table (user, date, score) + "Run new assessment".
 * Row action: View only (no Delete). Scope: organization; location/unit when stored.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import hospitalCapabilityProfileService from '../../services/hospitalCapabilityProfileService';
import assessmentResultsService from '../../services/assessmentResultsService';
import './AssessmentHistory.css';

const SOURCE_PROFILE = 'profile';
const SOURCE_ASSESSMENT_RESULTS = 'assessment_results';

function formatDate(d) {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' }) + ' ' + date.toLocaleTimeString(undefined, { timeStyle: 'short' });
}

function displayUser(run) {
  return run.completedByName || run.completedBy || '—';
}

function displayScore(run) {
  if (run.scorePercentage != null) return `${Math.round(run.scorePercentage)}%`;
  if (run.totalPercentage != null) return `${Math.round(run.totalPercentage)}%`;
  if (run.totalScore != null && run.maxPossibleScore != null) return `${run.totalScore} / ${run.maxPossibleScore}`;
  if (run.totalScore != null && run.totalApplicable != null) return `${run.totalScore} / ${run.totalApplicable}`;
  if (run.completionPercentage != null) return `${Math.round(run.completionPercentage)}%`;
  return '—';
}

const AssessmentHistoryList = ({
  organizationId,
  instrumentId,
  instrumentLabel,
  source,
  onRunNew,
  onViewRun,
  onViewCapabilityProfile,
}) => {
  const { t } = useTranslation(['qms', 'common']);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId || !instrumentId) {
      setRuns([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const fetchHistory = source === SOURCE_ASSESSMENT_RESULTS
      ? () => assessmentResultsService.getHistoryByOrganization(organizationId, instrumentId)
      : () => hospitalCapabilityProfileService.getAssessmentHistory(organizationId, instrumentId);
    fetchHistory()
      .then(setRuns)
      .catch((err) => {
        setError(err?.message || t('common:error'));
        setRuns([]);
      })
      .finally(() => setLoading(false));
  }, [organizationId, instrumentId, source, t]);

  if (!organizationId) {
    return (
      <div className="assessment-history">
        <p className="assessment-history-empty">{t('common:selectOrganization') || 'Select an organization.'}</p>
      </div>
    );
  }

  return (
    <div className="assessment-history">
      <div className="assessment-history-header">
        <h2 className="assessment-history-title">{instrumentLabel}</h2>
        <div className="assessment-history-actions">
          {onViewCapabilityProfile && (
            <button type="button" className="btn btn-secondary" onClick={onViewCapabilityProfile}>
              {t('qms:whoAssessments.viewCapabilityProfile') || 'Capability profile'}
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={onRunNew}>
            {t('qms:whoAssessments.runNewAssessment') || 'Run new assessment'}
          </button>
        </div>
      </div>

      {error && <div className="assessment-history-error">{error}</div>}

      {loading ? (
        <p className="assessment-history-loading">{t('common:loading') || 'Loading…'}</p>
      ) : runs.length === 0 ? (
        <div className="assessment-history-empty-card">
          <p>{t('qms:whoAssessments.noHistoryYet') || 'No assessment runs yet.'}</p>
        </div>
      ) : (
        <div className="assessment-history-table-wrap">
          <table className="assessment-history-table">
            <thead>
              <tr>
                <th>{t('qms:whoAssessments.user') || 'User'}</th>
                <th>{t('qms:whoAssessments.date') || 'Date'}</th>
                <th>{t('qms:whoAssessments.score') || 'Score'}</th>
                <th aria-label={t('common:actions') || 'Actions'} />
              </tr>
            </thead>
            <tbody>
              {runs.map((run, index) => (
                <tr key={run.id || index}>
                  <td>{displayUser(run)}</td>
                  <td>{formatDate(run.completedAt)}</td>
                  <td>{displayScore(run)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => onViewRun(run)}
                    >
                      {t('common:view') || 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssessmentHistoryList;
export { SOURCE_PROFILE, SOURCE_ASSESSMENT_RESULTS };
